export interface CityData {
  name: string;
  slug: string;
  state: string;
  population: string;
  practices: string;
  hospitals: string;
  medicalCenters: string;
  uniqueFact: string;
  stats: {
    practiceCount: string;
    cyberIncidents: string;
    avgDamage: string;
  };
  faqs: {
    question: string;
    answer: string;
  }[];
}

export const cityData: Record<string, CityData> = {
  berlin: {
    name: "Berlin",
    slug: "berlin",
    state: "Berlin",
    population: "3,7 Mio.",
    practices: "7.200",
    hospitals: "78",
    medicalCenters: "340 MVZ",
    uniqueFact: "Berlins Gesundheitssektor: 25% der Cyberangriffe betreffen Praxissoftware",
    stats: {
      practiceCount: "7.200",
      cyberIncidents: "+28% in 2024",
      avgDamage: "71.400 €"
    },
    faqs: [
      {
        question: "Was kostet eine Cyberversicherung für eine Arztpraxis in Berlin?",
        answer: "Die Kosten variieren je nach Praxisgröße und Leistungsumfang. Für eine durchschnittliche Berliner Praxis mit 3-5 Mitarbeitern beginnen die Tarife bei ca. 29€/Monat (348€/Jahr). Größere MVZ oder Kliniken zahlen entsprechend mehr, profitieren aber oft von Gruppenrabatten. Nutzen Sie unseren Tarifrechner für ein individuelles Angebot."
      },
      {
        question: "Gibt es in Berlin einen Vor-Ort-Service im Schadensfall?",
        answer: "Ja! Obwohl wir kein festes Büro in Berlin haben, arbeiten wir mit einem deutschlandweiten Netzwerk von IT-Forensik-Experten. Im Ernstfall kann innerhalb von 2-4 Stunden ein Spezialist bei Ihnen vor Ort sein. Zusätzlich bieten wir 24/7 Remote-Support über unsere Notfall-Hotline."
      },
      {
        question: "Deckt die Versicherung auch Zweigpraxen außerhalb von Berlin?",
        answer: "Ja, unsere Cyberversicherung gilt deutschlandweit. Wenn Sie neben Ihrer Hauptpraxis in Berlin weitere Standorte im Umland oder anderen Bundesländern betreiben, sind diese ebenfalls abgedeckt – Sie müssen dies nur bei Vertragsabschluss angeben."
      },
      {
        question: "Wie schnell bekomme ich nach einem Cyberangriff in Berlin Hilfe?",
        answer: "Unsere 24/7-Notfall-Hotline ist innerhalb von maximal 15 Minuten erreichbar. Ein IT-Forensik-Experte aus unserem Berlin-Partnernetzwerk wird dann umgehend kontaktiert. Durchschnittliche Vor-Ort-Reaktionszeit: 2-4 Stunden. Bei Remote-Support können wir oft innerhalb von 30 Minuten mit der Schadensanalyse beginnen."
      },
      {
        question: "Welche Cyberversicherung ist in Berlin besonders geeignet?",
        answer: "Für Berliner Praxen empfehlen wir je nach Größe unterschiedliche Anbieter: Hiscox und Markel bieten exzellente Leistungen für Einzelpraxen und kleine MVZ. HDI, AXA und Gothaer sind ideal für größere Einrichtungen und Kliniken. Wir vergleichen alle Top-Tarife und finden die beste Lösung für Ihre spezifischen Anforderungen in Berlin."
      },
      {
        question: "Wie schnell kann ich in Berlin eine Cyberversicherung abschließen?",
        answer: "Nach unserer Beratung können Sie innerhalb von 24-48 Stunden vollständig versichert sein. Die Antragsstellung erfolgt digital über unseren Tarifrechner oder per Beratungsgespräch. Viele Versicherer bieten Sofortdeckung nach Zahlungseingang – in dringenden Fällen ist ein Versicherungsschutz ab dem nächsten Werktag möglich."
      },
      {
        question: "Sind DSGVO-Bußgelder bei Berliner Praxen abgedeckt?",
        answer: "Ja, alle von uns vermittelten Tarife decken DSGVO-Bußgelder ab. Das ist besonders wichtig, da die Berliner Datenschutzbehörde zu den strengsten in Deutschland gehört. Bei Patientendaten-Lecks können Bußgelder schnell sechsstellige Beträge erreichen – unsere Versicherungen schützen Sie vor solchen existenzbedrohenden Kosten."
      },
      {
        question: "Was unterscheidet die Berliner Cyberversicherung von anderen Städten?",
        answer: "Berlin als Hauptstadt verzeichnet überdurchschnittlich viele Cyberangriffe auf Gesundheitseinrichtungen (+28% in 2024). Daher legen wir bei Berliner Praxen besonderen Wert auf schnelle Notfall-Reaktion, lokale Forensik-Partner und höhere Deckungssummen. Unsere Tarife sind speziell auf die erhöhten Risiken der Metropolregion zugeschnitten."
      }
    ]
  },
  muenchen: {
    name: "München",
    slug: "muenchen",
    state: "Bayern",
    population: "1,5 Mio.",
    practices: "5.800",
    hospitals: "52",
    medicalCenters: "280 MVZ",
    uniqueFact: "München hat die höchste Arztdichte Deutschlands mit 425 Ärzten pro 100.000 Einwohner",
    stats: {
      practiceCount: "5.800",
      cyberIncidents: "+32% in 2024",
      avgDamage: "68.200 €"
    },
    faqs: [
      {
        question: "Warum ist eine Cyberversicherung in München besonders wichtig?",
        answer: "München ist ein Hotspot für Cyberkriminalität im Gesundheitssektor. Mit der hohen Dichte an Privatpraxen und MVZ sind Münchner Einrichtungen bevorzugte Ziele für Ransomware-Angriffe. Eine Cyberversicherung schützt vor existenzbedrohenden Kosten."
      },
      {
        question: "Welche Schäden deckt eine Cyberversicherung in München ab?",
        answer: "Abgedeckt sind Datenwiederherstellung, IT-Forensik, Betriebsunterbrechung, DSGVO-Bußgelder, Rechtskosten und PR-Maßnahmen nach einem Cyberangriff. Auch die Benachrichtigung betroffener Patienten ist versichert."
      },
      {
        question: "Gibt es Präventionsmaßnahmen, die Münchner Praxen ergreifen sollten?",
        answer: "Ja, viele Versicherer bieten kostenlose Security-Audits, Mitarbeiterschulungen und Cyber-Risiko-Analysen an. Diese Maßnahmen können auch die Prämie reduzieren."
      }
    ]
  },
  hamburg: {
    name: "Hamburg",
    slug: "hamburg",
    state: "Hamburg",
    population: "1,9 Mio.",
    practices: "4.900",
    hospitals: "43",
    medicalCenters: "195 MVZ",
    uniqueFact: "Hamburgs Hafen ist ein Einfallstor für internationale Cyber-Bedrohungen",
    stats: {
      practiceCount: "4.900",
      cyberIncidents: "+24% in 2024",
      avgDamage: "64.800 €"
    },
    faqs: [
      {
        question: "Welche Cyberrisiken sind in Hamburg besonders relevant?",
        answer: "Hamburg verzeichnet eine hohe Anzahl an Phishing-Angriffen auf medizinische Einrichtungen. Besonders gefährdet sind Praxen mit älteren Softwaresystemen und unzureichenden Backup-Strategien."
      },
      {
        question: "Wie unterscheiden sich die Tarife für Hamburger Praxen?",
        answer: "Tarife variieren je nach Fachrichtung, Patientenzahl und IT-Infrastruktur. Hamburger Praxen profitieren von regionalen Versicherern wie der HUK-Coburg mit speziellen Norddeutschland-Tarifen."
      },
      {
        question: "Benötige ich als Einzelpraxis in Hamburg eine Cyberversicherung?",
        answer: "Absolut! Gerade Einzelpraxen sind attraktive Ziele, da sie oft weniger IT-Sicherheitsbudget haben. Eine Cyberversicherung schützt Sie vor existenzbedrohenden Kosten im Schadensfall."
      }
    ]
  },
  koeln: {
    name: "Köln",
    slug: "koeln",
    state: "Nordrhein-Westfalen",
    population: "1,1 Mio.",
    practices: "3.600",
    hospitals: "38",
    medicalCenters: "165 MVZ",
    uniqueFact: "Köln verzeichnet die meisten DSGVO-Verstöße im Gesundheitswesen in NRW",
    stats: {
      practiceCount: "3.600",
      cyberIncidents: "+26% in 2024",
      avgDamage: "59.300 €"
    },
    faqs: [
      {
        question: "Welche DSGVO-Risiken bestehen für Kölner Arztpraxen?",
        answer: "Bei Datenschutzverletzungen drohen Bußgelder bis zu 20 Mio. € oder 4% des Jahresumsatzes. Eine Cyberversicherung übernimmt diese Kosten sowie die rechtliche Beratung und Meldepflichten gegenüber Behörden."
      },
      {
        question: "Was kostet eine Cyberversicherung in Köln?",
        answer: "Für eine durchschnittliche Hausarztpraxis mit 1.500 Patienten und 1 Mio. € Deckung liegt die Prämie bei 900-1.400 € jährlich. Facharztpraxen zahlen je nach Spezialisierung 1.200-2.000 €."
      },
      {
        question: "Bieten Versicherer in Köln Vor-Ort-Beratung an?",
        answer: "Ja, wir vermitteln Ihnen regionale Makler mit Cyber-Spezialisierung, die kostenlose Risikoanalysen direkt in Ihrer Praxis durchführen."
      }
    ]
  },
  frankfurt: {
    name: "Frankfurt am Main",
    slug: "frankfurt",
    state: "Hessen",
    population: "760.000",
    practices: "2.800",
    hospitals: "29",
    medicalCenters: "140 MVZ",
    uniqueFact: "Als Finanzmetropole ist Frankfurt besonders im Visier internationaler Hacker-Gruppen",
    stats: {
      practiceCount: "2.800",
      cyberIncidents: "+35% in 2024",
      avgDamage: "73.900 €"
    },
    faqs: [
      {
        question: "Warum ist Frankfurt besonders von Cyberangriffen betroffen?",
        answer: "Frankfurts Status als Finanzmetropole macht die Stadt zum Hauptziel professioneller Cyberkrimineller. Medizinische Einrichtungen werden oft als 'Nebenopfer' oder gezielt wegen sensibler Gesundheitsdaten attackiert."
      },
      {
        question: "Welche Versicherungssumme ist für Frankfurter Praxen sinnvoll?",
        answer: "Wir empfehlen mindestens 1,5 Mio. € Deckung aufgrund der hohen Schadenssummen in Frankfurt. Für MVZ und größere Praxen sollten 3-5 Mio. € in Betracht gezogen werden."
      },
      {
        question: "Gibt es spezielle Cyber-Policen für Frankfurter Finanzdistrikt-Praxen?",
        answer: "Ja, Versicherer wie Allianz und Zurich bieten Premium-Tarife mit erweiterten Leistungen für Praxen in exponierten Lagen, inklusive Reputation Management und Krisen-PR."
      }
    ]
  },
  stuttgart: {
    name: "Stuttgart",
    slug: "stuttgart",
    state: "Baden-Württemberg",
    population: "635.000",
    practices: "2.400",
    hospitals: "26",
    medicalCenters: "125 MVZ",
    uniqueFact: "Stuttgart ist Vorreiter bei digitaler Patientenakte - und damit besonders schutzbedürftig",
    stats: {
      practiceCount: "2.400",
      cyberIncidents: "+29% in 2024",
      avgDamage: "61.700 €"
    },
    faqs: [
      {
        question: "Wie schützt eine Cyberversicherung Stuttgarter Praxen vor ePA-Risiken?",
        answer: "Die elektronische Patientenakte (ePA) erhöht das Risiko für Datenschutzverletzungen. Eine Cyberversicherung deckt Schäden durch unautorisierten Zugriff, Datenlecks und resultierende DSGVO-Bußgelder ab."
      },
      {
        question: "Welche Tarife sind für Baden-Württemberg besonders geeignet?",
        answer: "Württembergische Versicherung und NÜRNBERGER bieten regional optimierte Tarife mit Schwerpunkt auf digitale Gesundheitsanwendungen und Telemedizin-Absicherung."
      },
      {
        question: "Wie schnell greift der Versicherungsschutz in Stuttgart?",
        answer: "Die meisten Tarife bieten Sofortschutz ab Vertragsunterzeichnung. Die 24/7-Notfall-Hotline ist bereits am ersten Versicherungstag aktiv."
      }
    ]
  },
  duesseldorf: {
    name: "Düsseldorf",
    slug: "duesseldorf",
    state: "Nordrhein-Westfalen",
    population: "620.000",
    practices: "2.200",
    hospitals: "24",
    medicalCenters: "110 MVZ",
    uniqueFact: "Düsseldorf hat die höchste Dichte an Privatpraxen - ein lukratives Ziel für Cyberkriminelle",
    stats: {
      practiceCount: "2.200",
      cyberIncidents: "+27% in 2024",
      avgDamage: "66.500 €"
    },
    faqs: [
      {
        question: "Warum sind Düsseldorfer Privatpraxen besonders gefährdet?",
        answer: "Privatpraxen verwalten hochsensible Daten vermögender Patienten. Cyberkriminelle wissen, dass hier höheres Erpressungspotenzial besteht und setzen gezielt auf Ransomware-Angriffe."
      },
      {
        question: "Welche Zusatzleistungen sollten Düsseldorfer Praxen absichern?",
        answer: "Empfohlen sind: Reputation Management (wichtig für Privatpraxen), erweiterte Betriebsunterbrechungsversicherung und Cyber-Erpressungsschutz mit bis zu 250.000 € Lösegeld-Erstattung."
      },
      {
        question: "Gibt es Gruppenrabatte für MVZ in Düsseldorf?",
        answer: "Ja, viele Versicherer bieten Flottenrabatte für MVZ und Praxisgemeinschaften. Bei 3+ Standorten sind Prämienreduzierungen von 15-25% möglich."
      }
    ]
  },
  leipzig: {
    name: "Leipzig",
    slug: "leipzig",
    state: "Sachsen",
    population: "600.000",
    practices: "1.900",
    hospitals: "21",
    medicalCenters: "95 MVZ",
    uniqueFact: "Leipzig verzeichnet den stärksten Anstieg digitaler Praxisverwaltung in Ostdeutschland",
    stats: {
      practiceCount: "1.900",
      cyberIncidents: "+31% in 2024",
      avgDamage: "57.200 €"
    },
    faqs: [
      {
        question: "Wie entwickelt sich die Cyber-Bedrohungslage in Leipzig?",
        answer: "Leipzig zeigt mit +31% den stärksten Anstieg an Cyberangriffen in Sachsen. Besonders betroffen sind Praxen, die kürzlich auf digitale Systeme umgestellt haben und noch Sicherheitslücken aufweisen."
      },
      {
        question: "Welche Versicherer sind in Leipzig aktiv?",
        answer: "Alle großen Anbieter sind vertreten. Besonders empfehlenswert für Leipzig sind ERGO, Signal Iduna und R+V mit speziellen Ostdeutschland-Tarifen und lokalen Ansprechpartnern."
      },
      {
        question: "Gibt es Förderprogramme für IT-Sicherheit in Leipzig?",
        answer: "Die IHK Leipzig bietet Beratungen zu IT-Sicherheit an. Einige Versicherer gewähren Prämienrabatte, wenn Sie an zertifizierten Cyber-Security-Workshops teilnehmen."
      }
    ]
  },
  dresden: {
    name: "Dresden",
    slug: "dresden",
    state: "Sachsen",
    population: "560.000",
    practices: "1.700",
    hospitals: "19",
    medicalCenters: "85 MVZ",
    uniqueFact: "Dresden ist Standort mehrerer Universitätskliniken - attraktives Ziel für Forschungsdatendiebstahl",
    stats: {
      practiceCount: "1.700",
      cyberIncidents: "+23% in 2024",
      avgDamage: "55.800 €"
    },
    faqs: [
      {
        question: "Welche Besonderheiten gelten für Dresdner Universitätskliniken?",
        answer: "Universitätskliniken und Forschungseinrichtungen benötigen erweiterten Schutz für Forschungsdaten und klinische Studien. Spezielle Policen decken auch Industriespionage und Forschungsdatendiebstahl ab."
      },
      {
        question: "Was kostet eine Cyberversicherung in Dresden?",
        answer: "Für Einzelpraxen 750-1.200 € jährlich, für Gemeinschaftspraxen 1.400-2.200 €. Die Prämien sind in Dresden tendenziell 10-15% günstiger als in Westdeutschland bei gleicher Deckung."
      },
      {
        question: "Wie wichtig ist lokaler Support in Dresden?",
        answer: "Sehr wichtig! Wir vermitteln Versicherer mit sächsischen Niederlassungen, die im Schadensfall schnell vor Ort sind und die regionalen IT-Dienstleister kennen."
      }
    ]
  },
};

export const topCities = [
  "berlin",
  "muenchen",
  "hamburg",
  "koeln",
  "frankfurt",
  "stuttgart",
  "duesseldorf",
  "leipzig",
  "dresden"
];

export const allCities = Object.keys(cityData);
